// Vault picker modal — used by the chat composer's "From vault" action. Lists
// the caller's vault items and, on pick, hands the chosen item back so the
// composer can fetch its bytes and stage it exactly like a device upload.
import { useEffect, useState } from "react";

import { useI18n } from "~/i18n";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { fetchVaultItems, fetchVaultItemPreviewUrl, type VaultItem } from "../../morrowVault";
import { formatBytes, iconForMime, isImageMime, typeLabel } from "./vaultShared";

export function VaultPicker(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (item: VaultItem) => void;
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<VaultItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    setLoading(true);
    void fetchVaultItems().then((next) => {
      if (cancelled) return;
      setItems(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [props.open]);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup className="max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t("vault.pickTitle")}</DialogTitle>
          <DialogDescription>{t("vault.pickSubtitle")}</DialogDescription>
        </DialogHeader>
        <DialogPanel className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <p className="px-1 py-6 text-center text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          ) : items.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-muted-foreground">
              {t("vault.empty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {items.map((item) => {
                const Icon = iconForMime(item.mimeType);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        props.onPick(item);
                        props.onOpenChange(false);
                      }}
                      className="flex w-full items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-2.5 text-left shadow-xs transition-colors hover:border-border hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
                    >
                      <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/50 bg-muted text-muted-foreground">
                        {isImageMime(item.mimeType) ? (
                          <VaultPickerThumb item={item} icon={Icon} />
                        ) : (
                          <Icon className="size-5" aria-hidden="true" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {typeLabel(item.mimeType)} · {formatBytes(item.sizeBytes)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}

/**
 * The row thumbnail for an image item.
 *
 * The vault's content route is owner-scoped, so it only answers an authenticated fetch. A plain
 * `<img src={vaultItemContentUrl(...)}>` is not one — the browser issues it without the app's
 * credentials, the route refuses it, and every image in the picker rendered as a broken-link
 * glyph. Same object-URL dance as VaultCardPreview: fetch the bytes properly, hand the <img> a
 * blob URL, and revoke it when the row goes away. A fetch that fails falls back to the file-type
 * icon, so the worst case is the same placeholder a PDF gets rather than a broken image.
 */
function VaultPickerThumb(props: { item: VaultItem; icon: ReturnType<typeof iconForMime> }) {
  const { item } = props;
  const Icon = props.icon;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    void fetchVaultItemPreviewUrl(item).then((url) => {
      if (!active) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      createdUrl = url;
      setObjectUrl(url);
    });
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [item.id]);

  if (!objectUrl) return <Icon className="size-5" aria-hidden={true} />;
  return <img src={objectUrl} alt="" className="size-full object-cover" />;
}
